import { Component, Input, ViewChild, inject, OnInit } from '@angular/core';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { ActivatedRoute } from '@angular/router';
import { combineLatest } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { Enums } from 'src/app/Shared/enums';
import { SharedService } from 'src/app/Shared/shared-service.service';
import { CommissionService } from 'src/services/commission.service';
import { CompanyService } from 'src/services/company.service';
import { ReportService } from 'src/services/report.service';
import { SpinnerService } from 'src/services/spinner.service';
import { TransactionService } from 'src/services/transaction.service';

interface ChargesBalance {
  CompanyId: number,
  CompanyCode: string,
  StampBalance: number,
  ScBalance: number,
  ExtrasBalance: number,
  HandlingChargeBalance: number,
  UnpaidComm?: number,
  LatestCheckin?: Date
  CapitalBalance?: number
}

interface TransType {
  id: number,
  text: string,
  columnName: string
}

@Component({
    selector: 'app-charges-balance-summary',
    templateUrl: './charges-balance-summary.component.html',
    styleUrl: './charges-balance-summary.component.scss',
    standalone: false
})
export class ChargesBalanceSummaryComponent implements OnInit {
  private activatedRoute = inject(ActivatedRoute);
  protected enums = inject(Enums);
  private commService = inject(CommissionService);
  private companyService = inject(CompanyService);
  private reportServie = inject(ReportService);
  private transService = inject(TransactionService);
  protected sharedService = inject(SharedService);
  private spinnerService = inject(SpinnerService);

  @Input() selectedReport: number;

  searchText: string;
  capitalPercentage = 2.5;
  routeSubscription: any;
  companyGroup: number;
  companyId: number;
  endDate: Date;
  chargesBalanceList: ChargesBalance[] = [];
  readonly fixedColumns: string[] = ['CompanyCode', 'UnpaidComm', 'LatestCheckin'];
  displayedColumns: string[];
  transType: TransType[] = [
    { id: this.enums.CAPITAL, text: 'Capital Balance', columnName: 'CapitalBalance,CapitalInterest' },
    { id: this.enums.STAMP_OFFICE, text: 'Stamp Office Balance', columnName: 'StampBalance' },
    { id: this.enums.SERVICE_CHARGE, text: 'SC Balance', columnName: 'ScBalance' },
    { id: this.enums.EXTRAS, text: 'DL Balance', columnName: 'ExtrasBalance' },
    { id: this.enums.HANDLING_CHARGE, text: 'Handling Charge Balance', columnName: 'HandlingChargeBalance' }

  ]
  selectedTransType: string[];
  dataSource = new MatTableDataSource<ChargesBalance>();

  @ViewChild(MatPaginator, { static: true }) paginator: MatPaginator;
  @ViewChild(MatSort, { static: true }) sort: MatSort;

  ngOnInit(): void {
    this.selectedTransType = this.transType.map(t => t.columnName);
    this.toggleColumns();
    combineLatest([
      this.activatedRoute.params,
      this.activatedRoute.queryParams
    ]).pipe(
      /**
       * *Required: wait until both latest value
       * eg: /chargesBalanceSummary/1?companyId=1 -> /chargesBalanceSummary/2
       * with debounceTime: trigger once with final values: 2, undefined
       * without decounceTime: trigger twice: 1, undefined then 2, undefined
       * */
      debounceTime(0)
    ).subscribe(async ([params, queryParams]) => {
      this.companyGroup = +params['companyGroup'];
      this.companyId = !queryParams['companyId'] ? undefined : +queryParams['companyId'];
      this.endDate = !queryParams['endDate'] ? undefined : new Date(queryParams['endDate']);
      this.spinnerService.show();
      await this.getTransactionBalances();
      this.spinnerService.hide();
      this.chargesBalanceList.sort((a, b) => (a.CompanyCode > b.CompanyCode) ? 1 : -1);
      this.dataSource = new MatTableDataSource<ChargesBalance>(this.chargesBalanceList);
      this.dataSource.paginator = this.paginator;
      this.dataSource.sort = this.sort;
    });
  }

  toggleColumns() {
    const t = this.selectedTransType.flatMap(t => t.split(','));
    this.displayedColumns = [this.fixedColumns[0], ...t, ...this.fixedColumns.slice(1)];
  }

  applyFilter(event: any) {
    const filterValue = (event.target as HTMLInputElement).value;
    this.dataSource.filter = filterValue.trim().toLowerCase();
  }

  async getTransactionBalances() {
    const [company, lastCheckIn, balances, unpaidComm] = await Promise.all([
      this.companyService.getAllCompanies(),
      this.reportServie.getCompaniesLatestCheckIns(),
      this.transService.getTransactionBalances({ companyGroup: this.companyGroup, companyId: this.companyId, endDate: this.endDate, types: [this.enums.STAMP_OFFICE, this.enums.SERVICE_CHARGE, this.enums.EXTRAS, this.enums.HANDLING_CHARGE, this.enums.CAPITAL] }),
      this.commService.getAllUnpaidCommissionByCompany(this.companyId)
    ]);
    const r = this.sharedService.groupByNumKey(balances, 'companyId');
    this.chargesBalanceList = Object.keys(r).map(key => {
      const k = Number(key);
      return {
        CompanyId: k,
        CompanyCode: company.find(c => c.ID === k).CompanyCode,
        StampBalance: r[k].find(t => t.type == this.enums.STAMP_OFFICE)?.balance ?? 0,
        ScBalance: r[k].find(t => t.type == this.enums.SERVICE_CHARGE)?.balance ?? 0,
        ExtrasBalance: r[k].find(t => t.type == this.enums.EXTRAS)?.balance ?? 0,
        HandlingChargeBalance: r[k].find(t => t.type == this.enums.HANDLING_CHARGE)?.balance ?? 0,
        CapitalBalance: r[k].find(t => t.type == this.enums.CAPITAL)?.balance ?? 0,
        UnpaidComm: unpaidComm.get(k) ?? 0,
        LatestCheckin: lastCheckIn.find(l => l.CompanyID === k)?.Date
      } as ChargesBalance
    });
  }
}
